import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CheckboxGroupComponent,
	DatepickerComponent,
	FormGroupComponent,
	InputComponent,
	ISelect,
	SelectComponent,
	SwitchComponent,
	TextareaComponent,
	ToggleAcceptComponent,
	ToggleComponent,
	UploadComponent,
} from '@tecalisdev/infinity';
import { IToggleAccept } from '@tecalisdev/infinity/lib/interfaces/toggleAccept';
import { PhoneInputComponent } from 'src/app/shared/components/phone-input/phone-input.component';
import { IFormFieldConfig } from '../../../models/user-identification';
import {
	dynamicFormFieldGridColClass,
	dynamicFormFieldGroupLabel,
	dynamicFormFieldPlaceholder,
	dynamicFormInputType,
	dynamicFormLegalAcceptContentLabel,
	dynamicFormLegalControlKey,
	dynamicFormLegalOptionLabel,
	isDynamicFormDateField,
	isDynamicFormFieldMultiple,
	isDynamicFormFieldRequired,
	isDynamicFormNumberField,
	isDynamicFormPhoneField,
	isDynamicFormYesNoField,
	usesDynamicFormBooleanCheckbox,
	usesDynamicFormCheckboxGroup,
	usesDynamicFormChoiceToggle,
	usesDynamicFormLegalField,
	usesDynamicFormSelectDropdown,
	usesDynamicFormToggleField,
} from '../../../utils/dynamic-form-field.util';

@Component({
	selector: 'app-dynamic-form-field',
	imports: [
		NgClass,
		ReactiveFormsModule,
		TranslatePipe,
		FormGroupComponent,
		InputComponent,
		TextareaComponent,
		SelectComponent,
		SwitchComponent,
		ToggleComponent,
		CheckboxGroupComponent,
		UploadComponent,
		DatepickerComponent,
		ToggleAcceptComponent,
		PhoneInputComponent,
	],
	templateUrl: './dynamic-form-field.component.html',
	styleUrl: './dynamic-form-field.component.scss',
})
export class DynamicFormFieldComponent {
	@Input({ required: true }) field!: IFormFieldConfig;
	@Input({ required: true }) stepForm!: FormGroup;
	@Input({ required: true }) stepTitle = '';
	@Input({ required: true }) resolveLocalizedText!: (value?: unknown) => string;
	@Input() options: ISelect[] = [];
	@Input() selectPlaceholder = '';
	@Input() selectSearch = false;
	@Input() selectDisabled = false;
	@Input() legalToggleOptions: Record<string, IToggleAccept[]> = {};
	@Input() visibleFieldCount = 1;
	/** Errores visibles solo tras Continuar (NIF-824). */
	@Input() showValidationErrors = false;

	@Output() legalToggleChange = new EventEmitter<{ controlKey: string; options: IToggleAccept[] }>();
	@Output() fileSelected = new EventEmitter<{ fieldName: string; files: FileList }>();

	private trans = inject(TranslateService);

	public gridColClass(): string {
		return dynamicFormFieldGridColClass(this.field);
	}

	public groupLabel(): string {
		// Sí/No, checkbox y legal: copy fijo «Indica tu elección»; el required solo añade `*` (NIF-824).
		if (this.usesChoicePromptLabel()) {
			const label = this.trans.instant('FORM.VALIDATION.REQUIRED_CHOICE');
			return isDynamicFormFieldRequired(this.field) ? `${label}*` : label;
		}
		return dynamicFormFieldGroupLabel(this.field, this.stepTitle, this.resolveLocalizedText, {
			optionCount: this.options.length,
			visibleFieldCount: this.visibleFieldCount,
		});
	}

	public placeholder(): string {
		return dynamicFormFieldPlaceholder(this.field, this.resolveLocalizedText);
	}

	public inputType(): ReturnType<typeof dynamicFormInputType> {
		return dynamicFormInputType(this.field);
	}

	public hasError(): boolean {
		const control = this.stepForm.get(this.field.name);
		return Boolean(control && control.invalid && this.showValidationErrors);
	}

	public errorMessage(): string {
		const control = this.stepForm.get(this.field.name);
		if (!control?.errors || !this.hasError()) {
			return '';
		}
		if (control.errors['email']) {
			return this.trans.instant('FORM.VALIDATION.INVALID_EMAIL');
		}
		if (
			isDynamicFormPhoneField(this.field) &&
			(control.errors['phoneE164'] || control.errors['pattern'])
		) {
			return this.trans.instant('FORM.VALIDATION.INVALID_PHONE');
		}
		if (control.errors['minlength'] || control.errors['required']) {
			return this.trans.instant(this.requiredErrorKey());
		}
		return '';
	}

	public hasLegalControlError(controlKey: string): boolean {
		const control = this.stepForm.get(controlKey);
		return Boolean(control && control.invalid && this.showValidationErrors);
	}

	public legalControlErrorMessage(controlKey: string): string {
		const control = this.stepForm.get(controlKey);
		if (!control?.errors || !this.hasLegalControlError(controlKey)) {
			return '';
		}
		if (control.errors['required'] || control.errors['requiredTrue']) {
			return this.trans.instant('FORM.VALIDATION.REQUIRED_CHOICE');
		}
		return '';
	}

	public requiresToggleSelection(field: IFormFieldConfig): boolean {
		return !isDynamicFormFieldMultiple(field) && isDynamicFormFieldRequired(field);
	}

	public legalOptionLabel(option: { label: unknown }): string {
		return dynamicFormLegalOptionLabel(
			this.field,
			{ label: option.label as never },
			this.resolveLocalizedText,
		);
	}

	/** Texto del toggle legal sin opciones: description o title (NIF-824). */
	public legalAcceptContentLabel(): string {
		return dynamicFormLegalAcceptContentLabel(this.field, this.resolveLocalizedText);
	}

	public legalControlKey(optionValue: string): string {
		return dynamicFormLegalControlKey(this.field, optionValue);
	}

	public getLegalToggleOptions(controlKey: string): IToggleAccept[] {
		return this.legalToggleOptions[controlKey] ?? [];
	}

	public onLegalToggleChange(controlKey: string, options: IToggleAccept[]): void {
		this.legalToggleChange.emit({ controlKey, options });
	}

	public onUploadFiles(files: FileList): void {
		this.fileSelected.emit({ fieldName: this.field.name, files });
	}

	public usesSelectDropdown(): boolean {
		return usesDynamicFormSelectDropdown(this.field, this.options.length);
	}

	public usesChoiceToggle(): boolean {
		return usesDynamicFormChoiceToggle(this.field, this.options.length);
	}

	public usesCheckboxGroup(field: IFormFieldConfig): boolean {
		return usesDynamicFormCheckboxGroup(field);
	}

	public readonly usesLegalField = usesDynamicFormLegalField;
	public readonly usesBooleanCheckbox = usesDynamicFormBooleanCheckbox;
	public readonly usesToggleField = usesDynamicFormToggleField;
	public readonly isFieldMultiple = isDynamicFormFieldMultiple;
	public readonly isDateField = isDynamicFormDateField;
	public readonly isNumberField = isDynamicFormNumberField;
	public readonly isPhoneField = isDynamicFormPhoneField;
	public readonly isFieldRequired = isDynamicFormFieldRequired;
	public readonly usesYesNoField = isDynamicFormYesNoField;

	/** Clave i18n de required: elección (sí/no, checkbox, toggle) vs campo texto. */
	private requiredErrorKey(): string {
		if (this.usesChoicePromptLabel() || this.usesChoiceToggle()) {
			return 'FORM.VALIDATION.REQUIRED_CHOICE';
		}
		return 'FORM.VALIDATION.REQUIRED';
	}

	/** Label fijo «Indica tu elección» (NIF-824); el `*` va solo si el campo es required. */
	private usesChoicePromptLabel(): boolean {
		return (
			usesDynamicFormLegalField(this.field) ||
			isDynamicFormYesNoField(this.field) ||
			usesDynamicFormCheckboxGroup(this.field)
		);
	}
}
